import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Breadcrumbs, Link, IconButton } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditStudent from "./EditStudent";
import EditIcon from '@mui/icons-material/Edit';
import AssignmentPill from "./AssignmentPill";
import { formatDay, formatHumanTime } from "./helpers";
import useSortHead from './useSortHead';
import { Button } from '@mui/material';
import { useAppState } from './AppStateContext';

function LinkRouter(props) {
  return <Link {...props} component={RouterLink} />;
}

function ClassStudent() {
    const { classId, studentId } = useParams();
    const { apiGet } = useAppState();
    const navigate = useNavigate();
    const location = useLocation();
    const className = location.state?.className;
    const { TableCellSortHead, sortFunction } = useSortHead();

    const [studentData, setStudentData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    const fetchStudentData = async () => {
        setErrorMessage(null);
        try {
            const response = await apiGet(`classes/${classId}/students/${encodeURIComponent(studentId)}`);
            setStudentData(response.data);
        } catch (error) {
            console.error('Failed to fetch student data', error);
            if (error.response?.status === 404) {
                navigate(`/classes/${classId}`, { replace: true });
            } else {
                setErrorMessage('An error occurred while trying to load the student data.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
        });
        // A different student is shown: clear the page and show the spinner.
        // Refetches for the same student keep the current page visible.
        setStudentData(null);
        setIsLoading(true);
        fetchStudentData();
    }, [classId, studentId]);

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress />
            </div>
        );
    }

    const handleClose = (modified) => {
        setEditDialogOpen(false);
        if (modified) {
            fetchStudentData();
        }
    };

    const handleEdit = () => {
        setEditDialogOpen(true);
    }

    const sortedAssignments = (studentData?.assignments ? [...studentData.assignments] : []).sort(sortFunction);

    // The server only computes the in-range figures when the class has a
    // validity range; their presence is what signals to use them, mirroring
    // the columns of the class's student table.
    const hasRange = Boolean(studentData) && studentData.total_time_in_range !== undefined;
    const totalInput = hasRange ? studentData.total_time_in_range : studentData?.total_time;
    const activeStudyTime = hasRange ? studentData.active_study_time_in_range : studentData?.active_study_time;

    return (
        <div>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                <LinkRouter underline="hover" color="primary" to="/classes">Classes</LinkRouter>
                <LinkRouter underline="hover" color="primary" to={`/classes/${classId}`}>{className}</LinkRouter>
                <Typography color="text.primary">{studentData?.name}</Typography>
            </Breadcrumbs>

            <h1>{studentData?.name}
                {studentData && (
                    <IconButton edge="end" sx={{ color: 'grey', ml: 1, mb: '3px' }} onClick={handleEdit} aria-label="edit">
                        <EditIcon size="28" />
                    </IconButton>
                )}
            </h1>

            {studentData && (
                <p>
                    Total input: <b>{formatHumanTime(totalInput) || 'none'}</b>.
                    Active study time: <b>{formatHumanTime(activeStudyTime) || 'none'}</b>.
                    {hasRange && ' The times cover the class’s active period.'}
                </p>
            )}

            <h2>Assignments</h2>

            <p>Below are assignments and progress. Students tap "Mark as Read" to log progress. Completion percentage is color-coded based on the assignment's audio length:</p>
            <p>            <Button
                variant="contained"
                size="small"
                disableElevation
                disabled
                sx={{
                    color: '#333333',
                    backgroundColor: '#FFF7AC',
                    '&:hover': {
                        backgroundColor: '#FFF7AC',
                    },
                    '&.Mui-disabled': {
                        color: '#333333',
                        backgroundColor: '#FFF7AC',
                    },
                    fontWeight: 400,
                    fontSize: '100%',
                    padding: '3px 15px',
                }}
            >Yellow:</Button> completed faster than audio (e.g., 5-min read for a 25-min audio).</p>
            <p><Button
                variant="contained"
                size="small"
                disableElevation
                disabled
                sx={{
                    color: '#333333',
                    backgroundColor: '#93DC9A',
                    '&:hover': {
                        backgroundColor: '#93DC9A',
                    },
                    '&.Mui-disabled': {
                        color: '#333333',
                        backgroundColor: '#93DC9A',
                    },
                    fontWeight: 400,
                    fontSize: '100%',
                    padding: '3px 15px',
                }}
            >Green:</Button> equal to or longer than audio.</p>

            {errorMessage && (
                <p style={{ color: 'red', padding: '20px' }}>{errorMessage}</p>
            )}
            {studentData && (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCellSortHead field='due_date'>Due date</TableCellSortHead>
                                <TableCellSortHead field='expected_study_time'>Minimum expected time</TableCellSortHead>
                                <TableCellSortHead field='study_time'>Active study time</TableCellSortHead>
                                <TableCellSortHead field='progress'>Progress</TableCellSortHead>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedAssignments.map(assignment => (
                                <TableRow
                                    key={assignment.uuid}
                                >
                                    <TableCell>{formatDay(assignment.due_date)}</TableCell>
                                    <TableCell>{formatHumanTime(assignment.expected_study_time)}</TableCell>
                                    <TableCell>{formatHumanTime(assignment.study_time)}</TableCell>
                                    <TableCell><AssignmentPill assignment={assignment} /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <EditStudent open={editDialogOpen} onClose={handleClose} studentData={studentData} classId={classId} />
        </div>
    );
}

export default ClassStudent;
